import React from 'react';
import { Column } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { PmsComponent } from './useData';
import {
  pmsCriticalityField,
  pmsLookupSelect,
  pmsRecordStatusField,
  pmsSystemField,
  pmsVesselField} from '../shared/pmsFormFields';
import { getPmsToolbarIcon } from '../shared/pmsToolbarIcon';
import { createPmsGetTableActions } from '../shared/pmsTableActions';

export const moduleConfig = {
  module: {
    title: "Components",
    permissionModuleKey: 'pms_hierarchy',
    route: {
      list: '/pms/pms-components',
      new: '/pms/pms-components/new',
      detail: (id: number) => `/pms/pms-components/${id}`,
      edit: (id: number) => `/pms/pms-components/${id}/edit`},
    api: { list: '/pms-components', detail: (id: number) => `/pms-components/${id}` }},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search…' },
  filterGroups: [],
  filters: { fields: [], applyFilter: (items: PmsComponent[]) => items ,
  },
  toolbar: {
    actions: [{ key: 'add', icon: 'add', title: 'New', showTitle: false, variant: 'primary' as const, href: '/pms/pms-components/new' }]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: PmsComponent) => item.id,
  },
  emptyStates: { noData: 'No records.', noSearchResults: 'No matches.', noFilterResults: 'No matches.' },
  delete: {
    title: 'Delete',
    message: 'Delete this record?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete.',
    handler: async (id: number) => {
      const { deletePmsComponent } = await import('../../../services/mutations');
      await deletePmsComponent(id);
    }},
  utils: { formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '') },
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'Not found.',
    notFoundBackText: 'Back',
    defaultTitle: "Components",
    getTitle: (item: PmsComponent) => item.name ?? `#${item.id}`},
  form: {
    new: { title: 'New component', submitText: 'Create' },
    edit: { title: 'Edit component', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    formMaxWidth: 'max-w-4xl',
    create: async (data: Partial<PmsComponent>) => {
      const { createPmsComponent } = await import('../../../services/mutations');
      const {
        vesselId: _vesselId,
        vessel,
        system,
        criticality,
        recordStatus,
        parentComponent,
        id,
        ...payload
      } = data as Record<string, unknown>;
      return createPmsComponent(payload);
    },
    update: async (id: number, data: Partial<PmsComponent>) => {
      const { updatePmsComponent } = await import('../../../services/mutations');
      const {
        vesselId: _vesselId,
        vessel,
        system,
        criticality,
        recordStatus,
        parentComponent,
        id: _id,
        ...payload
      } = data as Record<string, unknown>;
      return updatePmsComponent(id, payload);
    }},
  fields: [
    {
      ...pmsVesselField(false),
      required: false,
      clearable: true,
      helperText: 'Optional filter — narrows the equipment system list',
      onChangeSideEffect: (_value, setFieldValue) => {
        setFieldValue('systemId', '');
        setFieldValue('parentComponentId', '');
      }},
    {
      ...pmsSystemField(true),
      helperText: 'System this component belongs to in the PMS hierarchy',
      onChangeSideEffect: (_value, setFieldValue) => {
        setFieldValue('parentComponentId', '');
      }},
    {
      ...pmsLookupSelect({
        name: 'parentComponentId',
        label: 'Parent component',
        apiEndpoint: '/pms-components',
        required: false,
        clearable: true,
        placeholder: 'None — top-level component',
        optionsDependOnFields: ['systemId'],
        optionsEndpointFromFormData: () => '/pms-components',
        filterDynamicOptionItem: (item, fd) => {
          if (fd.id && Number(item.id) === Number(fd.id)) return false;
          if (fd.systemId && Number(item.systemId) !== Number(fd.systemId)) return false;
          return true;
        }}),
      helperText: 'Optional. Nest under another component in the same system'},
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'e.g. Turbocharger',
      validation: { required: true }},
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      rows: 3,
      placeholder: 'Optional notes about this component’s function'},
    {
      name: 'manufacturer',
      label: 'Manufacturer',
      type: 'text',
      placeholder: 'e.g. MAN Energy Solutions'},
    {
      name: 'model',
      label: 'Model',
      type: 'text',
      placeholder: 'e.g. TCA55'},
    {
      ...pmsCriticalityField(true),
      helperText: 'How critical this component is to the system'},
    {
      ...pmsRecordStatusField(true),
      helperText: 'Lifecycle state of this component record'},
    {
      name: 'isActive',
      label: 'Status',
      type: 'switch',
      switchLabel: 'Component is active'},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'placement',
        title: 'Placement',
        description: 'Where this component lives under an equipment system.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['vesselId', 'systemId', 'parentComponentId'],
        fieldSpans: {
          vesselId: 'md:col-span-4',
          systemId: 'md:col-span-4',
          parentComponentId: 'md:col-span-4'},
        collapsible: false},
      {
        id: 'identity',
        title: 'Component',
        description: 'Name and maker details as they appear in the hierarchy.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['name', 'criticalityId', 'description', 'manufacturer', 'model'],
        fieldSpans: {
          name: 'md:col-span-8',
          criticalityId: 'md:col-span-4',
          description: 'md:col-span-12',
          manufacturer: 'md:col-span-6',
          model: 'md:col-span-6'},
        collapsible: false},
      {
        id: 'status',
        title: 'Status',
        description: 'Record lifecycle and whether the component is in the active plan.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['recordStatusId', 'isActive'],
        fieldSpans: {
          recordStatusId: 'md:col-span-6',
          isActive: 'md:col-span-6'},
        collapsible: false},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

const codeName = (
  rel: { code?: string | null; name?: string | null } | null | undefined,
  id?: number | null,
) => {
  if (rel?.name) return rel.name;
  return rel?.code ?? (id != null ? `#${id}` : '-');
};

export const getColumns = (_formatDate: (dateString?: string) => string): Column<PmsComponent>[] => [
  { key: 'name', header: 'Name', sortable: true, render: (v) => <TextCell value={v != null ? String(v) : undefined} /> },
  {
    key: 'systemId',
    header: 'System',
    sortable: true,
    render: (_v, row) => <TextCell value={codeName(row.system, row.systemId)} />},
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v) => <TextCell value={v === false ? 'No' : 'Yes'} />},
];

export const getDetailSections = (item: PmsComponent, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'System', value: codeName(item.system, item.systemId) },
      { label: 'Criticality', value: codeName(item.criticality, item.criticalityId) },
      { label: 'Record status', value: codeName(item.recordStatus, item.recordStatusId) },
      { label: 'Parent component', value: codeName(item.parentComponent, item.parentComponentId) },
      { label: 'Name', value: item.name != null ? String(item.name) : '' },
      { label: 'Description', value: item.description != null ? String(item.description) : '-' },
      { label: 'Manufacturer', value: item.manufacturer != null ? String(item.manufacturer) : '-' },
      { label: 'Model', value: item.model != null ? String(item.model) : '-' },
      { label: 'Active', value: item.isActive !== false ? 'Yes' : 'No' },
      { label: 'Created', value: formatDate(item.createdAt) },
    ]},
];

export const getStatusBadges = () => null;
export const getInitialFormData = (): Partial<PmsComponent> => ({
  vesselId: undefined,
  systemId: undefined,
  criticalityId: undefined,
  recordStatusId: undefined,
  parentComponentId: undefined,
  name: '',
  description: '',
  manufacturer: '',
  model: '',
  isActive: true});
export const mapItemToFormData = (item: PmsComponent): Partial<PmsComponent> => ({
  vesselId:
    item.system && typeof item.system === 'object' && 'vesselId' in item.system
      ? Number((item.system as { vesselId?: number }).vesselId)
      : undefined,
  systemId: item.systemId != null ? Number(item.systemId) : undefined,
  criticalityId: item.criticalityId != null ? Number(item.criticalityId) : undefined,
  recordStatusId: item.recordStatusId != null ? Number(item.recordStatusId) : undefined,
  parentComponentId: item.parentComponentId != null ? Number(item.parentComponentId) : undefined,
  name: item.name ?? '',
  description: item.description ?? '',
  manufacturer: item.manufacturer ?? '',
  model: item.model ?? '',
  isActive: item.isActive ?? true});
export const getToolbarIcon = getPmsToolbarIcon;
export const getTableActions = createPmsGetTableActions(moduleConfig.module.route);
